import classNames from "classnames";
import { isValidElement, Component } from "react";
import { _ } from "extensions/underscore";
import swal from "sweetalert";
import { findKey, startsWith } from "lodash-es";
import { ReactToMarionetteView, View } from "extensions/marionette";

import { store } from "common/store";
import strings from "common/strings";

import regularLogo from "img/logo@2x.png";

import { supportedEnvs } from "common/actions/settings";
import { pazienHistory } from "common/components/pazienHistory";
import { unsavedChangesHook } from "common/routers/unsavedChangesHook";

import { findLastInHistoryStack } from "common/helpers/historyStack";

import { PazienLogo } from "common/features/leftSidebar/sidebar";
import "common/features/leftSidebar/sidebar.scss";
import "./preferences.scss";
import _template from "./preferencesOverlay.jst";
import { TopBar } from "./preferencesTopbar";

class OverlaySidebar extends Component {
    constructor(props) {
        super(props);
        this.state = {
            route: props.route
        };
    }

    navigate = ({ route }) => (e) => {
        e.preventDefault();
        this.setState({
            route
        });
        pazienHistory.navigateWithParams(route, { trigger: true });
    };

    getUserSettings = () => {
        const user_settings = [];
        
        user_settings.push({
            route: "preferences/profile",
            text: "Profile",
            icon: "far fa-user"
        }, {
            route: "preferences/preferences",
            text: "Preferences",
            icon: "far fa-key"
        });
        
        if (store.getState().permissions.user_permissions.can_change_password) {
            user_settings.push({
                route: "preferences/security",
                text: "Change password",
                icon: "far fa-key"
            });
        }

        return user_settings;
    };

    getAdminSettings = () => {
        const admin_settings = [];
        
        if (store.getState().permissions.user_permissions.can_view_users) {
            admin_settings.push({
                route: "preferences/users",
                text: strings.userManagement,
                icon: "fal fa-cog"
            });
        }
        
        if (store.getState().permissions.user_permissions.can_view_data_availability) {
            admin_settings.push({
                route: "preferences/data",
                text: "Data status",
                icon: "far fa-calendar-check"
            });
        }
        
        if (store.getState().permissions.user_permissions.has_bu_manager) {
            admin_settings.push({
                route: "preferences/bumanager",
                text: "Business unit manager",
                icon: "far fa-sitemap"
            });
        }

        return admin_settings;
    };

    renderPageLink = (page) => (
        <div key={`page_${page.route || page.text}`} className="page-link">
            <a
                onClick={this.navigate(page)}
                className={classNames({ active: page.route && startsWith(this.state.route, page.route) })}
                href="#"
            >
                <span className="page-name">{page.text}</span>
            </a>
        </div>
    );

    render() {
        // const pages = [];
        const userPages = this.getUserSettings() || [];
        const adminPages = this.getAdminSettings() || [];

        return (
            <div className="sidebar-react">
                <div className="top">
                    <PazienLogo
                        className="logo"
                        env={findKey(supportedEnvs, (v) => v === store.getState().config.api_host)}
                        onClick={() => this.navigate({ route: `/${this.props.admin ? "administrator/credentials" : "merchant/dashboard"}` })}
                        href={`/${this.props.admin ? "administrator/credentials" : "merchant/dashboard"}`}
                    >
                        <img src={regularLogo} alt="Pazien" />
                    </PazienLogo>
                    <div className="pages">
                        {userPages.length ? <div className="section-header">User settings</div> : null}
                        {userPages.map(this.renderPageLink)}
                        {adminPages.length ? <div className="section-header">Admin tools</div> : null}
                        {adminPages.map(this.renderPageLink)}
                    </div>
                </div>
            </div>
        );
    }
}

export const PreferencesOverlay = View.extend({
    name: "Your account",
    className: "pz-overlay-layout",
    template: _template,
    ui: {
        sidebar: ".overlay-sidebar",
        closeButton: ".close-overlay",
        subpageName: ".subpage-name"
    },
    regions: {
        topbar: ".topbar",
        content: ".overlay-content",
        sidebar: ".overlay-sidebar",
        datepicker: ".sdr-datepicker"
    },
    events: {
        "click @ui.sidebar .section-link": "navigateTo"
    },
    childViewEvents: {
        closeOverlay: "closeOverlay",
        setSubPageName: "setSubPageName"
    },
    
    initialize() {
        this.hasUnsavedChanges = {};
        this.checkUnsavedChanges = this.checkUnsavedChanges.bind(this);
    },
    
    setSubPageName() {
        this.ui.subpageName.text("Business unit manager: Fixed hierarchy");
    },
    
    showContent(view, options = {}) {
        if (this.isDestroyed() || !this.getRegion("content")) {
            return;
        }
    
        if (isValidElement(view)) {
            view = new ReactToMarionetteView({
                component: view,
                ...options
            });
        }
        
        this.viewName = view.getOption("name");
        this.render();
        this.showChildView("content", view);

        if (this.viewName) {
            this.ui.subpageName.text(view.getOption("name"));
        }
    },
    
    checkUnsavedChanges(e) {
        e.preventDefault();
        if (unsavedChangesHook()) {
            if (_.some(this.hasUnsavedChanges, (value) => value)) {
                swal({
                    title: strings.preferences.unsavedChangesTitle,
                    text: strings.preferences.unsavedChanges,
                    icon: "warning",
                    buttons: {
                        confirm: {
                            text: strings.preferences.leave,
                            className: "btn-confirm"
                        },
                        cancel: {
                            visible: true
                        }
                    }
                }).then((confirmed) => {
                    if (confirmed) {
                        this.closeOverlay();
                    }
                });
            } else {
                this.closeOverlay();
            }
        }
    },
    
    closeOverlay() {
        // Need to find last not "preferences" page to navigate on close
        // This scenario cannot be changed to support controller call and then replacing history hash.
        const { fragment } = findLastInHistoryStack((route) => !route.fragment.includes("preferences/")) || {};
        if (typeof fragment !== "undefined") {
            pazienHistory.navigateWithParams(fragment, { trigger: true, replace: true });
            this.destroy();
        } else {
            const startPage = store.getState().user_merchant_settings.start_page || store.getState().config.defaultRouteMerchant;
            pazienHistory.navigateWithParams(startPage, {
                trigger: true,
                replace: true
            });
        }
    },
    
    renderSidebar() {
        this.showChildView("sidebar", <OverlaySidebar route={pazienHistory.getCurrentFragment()} />);
    },

    renderTopBar() {
        this.showChildView("topbar", <TopBar closeSettings={this.checkUnsavedChanges} />);
    },
    
    onRender() {
        document.body.classList.add("noscroll");
        document.body.appendChild(this.el);
        
        this.renderSidebar();
        this.renderTopBar();
    },
    
    onDestroy() {
        document.body.classList.remove("noscroll");
    }
});
